import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import { apiInstance } from '../services/service-trips-api/service-trips-api';
import type {
  CreateInstitutionCostCenterAccountRequest,
  TravelAddressRequest,
  TravelAddressSearchParams,
} from '../services/service-trips-api/types';
import { QueryKeys } from './types';

export const useCreateInstitutionCostCenterAccountMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreateInstitutionCostCenterAccountRequest) =>
      apiInstance.createInstitutionCostCenterAccount(payload),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [QueryKeys.Agreements] }),
        queryClient.invalidateQueries({ queryKey: [QueryKeys.AddressInstitutions] }),
      ]);
    },
  });
};

export const useTravelAddressesQuery = (params: TravelAddressSearchParams) => {
  const requestParams: TravelAddressSearchParams = {
    query: params.query,
    page: params.page ?? 0,
    size: params.size ?? 10,
  };

  return useQuery({
    queryKey: [
      QueryKeys.TravelAddresses,
      requestParams.query ?? '',
      requestParams.page,
      requestParams.size,
    ],
    queryFn: () => apiInstance.getTravelAddresses(requestParams),
    placeholderData: keepPreviousData,
    staleTime: 60_000,
  });
};

const useInvalidateTravelAddressQueries = () => {
  const queryClient = useQueryClient();
  return () =>
    Promise.all([
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.TravelAddresses],
      }),
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.Locations],
      }),
    ]);
};

export const useCreateTravelAddressMutation = () => {
  const invalidate = useInvalidateTravelAddressQueries();
  return useMutation({
    mutationFn: (payload: TravelAddressRequest) =>
      apiInstance.createTravelAddress(payload),
    onSuccess: invalidate,
  });
};

export const useUpdateTravelAddressMutation = () => {
  const invalidate = useInvalidateTravelAddressQueries();
  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: number;
      payload: TravelAddressRequest;
    }) => apiInstance.updateTravelAddress(id, payload),
    onSuccess: invalidate,
  });
};
